// Hover tooltip content: PLZ, name, canton, elevation, this value, reference value, difference —
// or, for a pairwise metric, the travel-time sentence. Either way, a "not within <band>" line is
// appended whenever the "Reachable within" filter is active and this PLZ falls outside it
// (docs/design-isochrones.md §3.3, WP10 brief).
import type { MetricFile, PlzIndex, RegistryMetric } from "../registry";
import { difference, formatBandLabel, valueFor, type MonthSelection, type ReachValue } from "../logic";
import { escapeHtml, formatDiff, formatValue } from "./format";

export interface TooltipData {
  plz: string;
  index: PlzIndex;
  refPlz: string;
  metric: RegistryMetric;
  metricFile: MetricFile | undefined;
  month: MonthSelection;
  /** Present whenever the pairwise matrix loaded for the current reference, regardless of
   * which metric is active — the reach filter dims every metric, not just the travel-time one. */
  pairwise?: {
    /** This PLZ's decoded travel time from the reference, if the matrix loaded. */
    value: ReachValue | undefined;
    /** Active "Reachable within" band, in minutes; 0 (or absent reach info) means "Any" (off). */
    bandMinutes: number;
  };
}

function formatPairwiseValue(v: ReachValue | undefined): string {
  if (v === undefined || v === null) return "no data";
  if (v === "beyond") return "> 8 h";
  return `${Math.round(v)} min`;
}

function pairwiseBody(data: TooltipData): string {
  const refRecord = data.index[data.refPlz];
  const v = data.pairwise?.value;
  const valueText = formatPairwiseValue(v);
  const refName = refRecord ? `${data.refPlz} ${refRecord.name}` : data.refPlz;
  return `<div class="tooltip-row">${valueText} by public transport from ${escapeHtml(refName)} (average weekday, door to door)</div>`;
}

function weatherBody(data: TooltipData): string {
  const dimSpec = data.metric.dims.month;
  const v = valueFor(data.metricFile?.values[data.plz], data.month, dimSpec);
  const rv = valueFor(data.metricFile?.values[data.refPlz], data.month, dimSpec);
  const d = difference(v, rv);
  const { decimals, unit } = data.metric;
  return `
    <div class="tooltip-row">${escapeHtml(data.metric.short)}: ${formatValue(v, decimals, unit)}</div>
    <div class="tooltip-row">Ref (${data.refPlz}): ${formatValue(rv, decimals, unit)}</div>
    <div class="tooltip-row tooltip-diff">Diff: ${formatDiff(d, decimals, unit)}</div>`;
}

/** "not within 2 h by public transport (134 min)", or null when the filter is off, the PLZ is
 * reachable, or there is no pairwise data for the current reference. */
function reachNote(data: TooltipData): string | null {
  const p = data.pairwise;
  if (!p || p.bandMinutes <= 0) return null;
  const v = p.value;
  const withinBand = typeof v === "number" && v <= p.bandMinutes;
  if (withinBand) return null;
  const detail = v === undefined || v === null ? "no data" : v === "beyond" ? "> 8 h" : `${Math.round(v)} min`;
  return `not within ${formatBandLabel(p.bandMinutes)} by public transport (${detail})`;
}

export function tooltipHtml(data: TooltipData): string | null {
  const record = data.index[data.plz];
  if (!record) return null; // geometry feature with no matching index entry: nothing to show

  const body = data.metric.kind === "pairwise" ? pairwiseBody(data) : weatherBody(data);
  const note = reachNote(data);
  const noteHtml = note ? `<div class="tooltip-row tooltip-reach">${escapeHtml(note)}</div>` : "";

  return `
    <div class="tooltip-title">${escapeHtml(record.name)} <span class="tooltip-plz">${data.plz}</span></div>
    <div class="tooltip-sub">${escapeHtml(record.canton)} · ${record.elev_ref} m</div>
    ${body}
    ${noteHtml}
  `;
}
